import state from './state.js';
import KEYS_BY_DIFFICULTY from './keys.json';
import config from './gameConfig.js';

const {
  KEY_HINT_DELAY_MS,
  KEY_TRIGGER_DELAY_MS,
  ROUNDS_NUMBER,
  LATIN_MAP,
  CYRILLIC_MAP,
  DIGIT_MAP,
} = config;

export default class Controller {
  constructor() {
    this.state = state;
  }

  /**
   * Sets event listeners.
   * @returns {void}
   */
  setListeners() {
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
    window.addEventListener('keyup', this.handleKeyUp.bind(this));
    window.addEventListener('mouseup', this.handleMouseUp.bind(this));
    window.addEventListener('touchend', this.handleTouchEnd.bind(this));
    window.addEventListener('changestart', this.handleChangeStart.bind(this));
    window.addEventListener(
      'changesequence',
      this.handleChangeSequence.bind(this)
    );
    window.addEventListener(
      'changeattempt',
      this.handleChangeAttempt.bind(this)
    );
    window.addEventListener('changeinput', this.handleChangeInput.bind(this));
    window.addEventListener('changeround', this.handleChangeRound.bind(this));
    window.addEventListener('changerepeat', this.handleChangeRepeat.bind(this));
  }

  /**
   * Handles pressed keyboard key.
   * @param {KeyboardEvent} e
   * @returns {void}
   * @private
   */
  handleKeyDown(e) {
    if (this.state.keydown || this.state.block || this.state.feedback) return;

    const value = e.key.toUpperCase();
    const mappedValue =
      DIGIT_MAP[value] || LATIN_MAP[value] || CYRILLIC_MAP[value];
    const validKeys = KEYS_BY_DIFFICULTY[this.state.difficulty];
    if (!validKeys.includes(mappedValue)) return;

    if (this.state.start) this.state.attempt = [mappedValue];

    this.state.keydown = { key: mappedValue, detail: 'keyboard' };
  }

  /**
   * Handles released keyboard key.
   * @param {KeyboardEvent} e
   * @returns {void}
   * @private
   */
  handleKeyUp(e) {
    const value = e.key.toUpperCase();
    const mappedValue =
      DIGIT_MAP[value] || LATIN_MAP[value] || CYRILLIC_MAP[value];

    const isKeyboardPressed = this.state.keydown?.detail === 'keyboard';
    const isValueEqual = this.state.keydown?.key === mappedValue;

    if (!isKeyboardPressed || !isValueEqual) return;

    this.state.keydown = null;
  }

  /**
   * Handles released mouse button.
   * @returns {void}
   * @private
   */
  handleMouseUp() {
    const isPointer = this.state.keydown?.detail === 'pointer';
    if (!isPointer) return;

    this.state.keydown = null;
  }

  /**
   * Handles screen touch end.
   * @returns {void}
   * @private
   */
  handleTouchEnd() {
    const isPointer = this.state.keydown?.detail === 'pointer';
    if (!isPointer) return;

    this.state.keydown = null;
  }

  /**
   * Generates a sequence corresponding to the current difficulty level and round.
   * @returns {string[]}
   */
  generateSequence() {
    const { difficulty, round } = this.state;

    /**
     * Returns a random item from array.
     * @template T
     * @param {T[]} array
     * @returns {T}
     */
    function getRandom(array) {
      const randomIndex = Math.floor(Math.random() * array.length);
      return array[randomIndex];
    }

    const sequence = [];
    while (sequence.length < round * 2) {
      sequence.push(getRandom(KEYS_BY_DIFFICULTY[difficulty]));
    }

    console.log(
      `%c✨ ${sequence.join(' ')} ✨`,
      'background: paleturquoise; color: midnightblue; padding: 10px 30px; border-radius: 4px; font-weight: 600; font-size: 20px'
    );

    return sequence;
  }

  /**
   * Handles changes in the state's start property.
   * @param {CustomEvent<Partial<StateObj>>} e
   * @returns {void}
   * @private
   */
  handleChangeStart(e) {
    const { start } = e.detail;

    if (start) {
      this.state.sequence = this.generateSequence();
    } else {
      this.state.feedback = null;
      this.state.repeat = true;
      this.state.round = 1;
      this.state.sequence = [];
      this.state.input = [];
      this.state.sequenceIndex = 0;
    }
  }

  /**
   * Handles changes in the state's sequence property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeSequence(e) {
    const { sequence } = e.detail;
    if (sequence.length > 0) this.triggerHint(sequence);
  }

  /**
   * Handles changes in the state's attempt property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeAttempt(e) {
    if (!this.state.start) return;

    const { attempt } = e.detail;
    const { sequence, sequenceIndex } = this.state;
    const attemptCorrect = sequence[sequenceIndex] === attempt[0];

    if (attemptCorrect) {
      this.state.sequenceIndex += 1;
    } else {
      if (this.state.repeat) {
        this.state.feedback = 'Incorrect!';
      } else {
        this.state.feedback = 'Too bad! You lose!';
      }
    }

    const input = [...this.state.input, attempt[0]];
    this.state.input = input;
  }

  /**
   * Handles changes in the state's input property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeInput(e) {
    const { input } = e.detail;
    const { round, sequence, sequenceIndex } = this.state;
    //
    const isRoundComplete =
      input.length > 0 && sequenceIndex === sequence.length;

    if (isRoundComplete) {
      let text;
      if (round === ROUNDS_NUMBER) {
        this.state.repeat = false;
        text = 'Congratulations! You win!';
      } else {
        text = `You've passed round ${this.state.round}!`;
      }
      this.state.feedback = text;
    }
  }

  /**
   * Handles changes in the state's round property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeRound(e) {
    const { round } = e.detail;
    if (round === 1) return;

    this.state.feedback = null;
    this.state.repeat = true;
    this.state.input = [];
    this.state.sequenceIndex = 0;
    this.state.sequence = this.generateSequence();
  }

  /**
   * Handles changes in the state's repeat property.
   * @param {CustomEvent<StateObj>} e
   * @returns {void}
   * @private
   */
  handleChangeRepeat(e) {
    const { repeat } = e.detail;

    const { input, sequence, sequenceIndex } = this.state;

    const isRoundComplete =
      input.length > 0 && sequenceIndex === sequence.length;

    if (!repeat && !isRoundComplete) {
      this.triggerHint(this.state.sequence);
      this.state.feedback = null;
      this.state.input = [];
      this.state.sequenceIndex = 0;
    }
  }

  /**
   * Plays a hint from consecutive key callbacks.
   * @param {string[]} sequence
   * @returns {void}
   * @private
   */
  triggerHint(sequence) {
    this.state.block = true;

    let initialDelay = KEY_HINT_DELAY_MS;
    for (let i = 0; i < sequence.length; i++) {
      const delay = initialDelay;
      document.body.classList.add('vignette');

      setTimeout(() => {
        this.state.keycallbacks[sequence[i]](
          KEY_HINT_DELAY_MS - KEY_TRIGGER_DELAY_MS
        );

        if (i === sequence.length - 1) {
          setTimeout(() => {
            this.state.block = false;
            document.body.classList.remove('vignette');
          }, KEY_HINT_DELAY_MS);
        }
      }, delay);

      initialDelay += KEY_HINT_DELAY_MS;
    }
  }
}
